import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { getSettings, updateSetting, updateLogo, removeLogo } from '../services/api';
import { showSuccess, showError, showConfirmDanger } from '../services/swal';

const Settings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeSection, setActiveSection] = useState('shop');
    const fileInputRef = useRef(null);

    // Settings state
    const [shopName, setShopName] = useState('');
    const [shopAddress, setShopAddress] = useState('');
    const [shopPhone, setShopPhone] = useState('');
    const [shopEmail, setShopEmail] = useState('');
    const [logoUrl, setLogoUrl] = useState('');
    const [billNote, setBillNote] = useState('');
    const [billBarcodeEnabled, setBillBarcodeEnabled] = useState(true);
    const [billLogoEnabled, setBillLogoEnabled] = useState(true);
    const [barcodeSize, setBarcodeSize] = useState('medium');
    const [customBarcodeW, setCustomBarcodeW] = useState('50');
    const [customBarcodeH, setCustomBarcodeH] = useState('25');
    
    // New Advanced Settings
    const [receiptPrefix, setReceiptPrefix] = useState('INV');
    const [taxRate, setTaxRate] = useState('0');
    const [currencyCode, setCurrencyCode] = useState('PKR');
    const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');

    // Module toggles
    const [moduleSuppliers, setModuleSuppliers] = useState(localStorage.getItem('module_suppliers') === 'enabled');
    const [moduleExpenses, setModuleExpenses] = useState(localStorage.getItem('module_expenses') === 'enabled');
    const [moduleTeam, setModuleTeam] = useState(localStorage.getItem('module_team') !== 'disabled');

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const res = await getSettings();
            const s = res.data;
            setShopName(s.company_name || '');
            setShopAddress(s.shop_address || '');
            setShopPhone(s.shop_phone || '');
            setShopEmail(s.shop_email || '');
            setLogoUrl(s.company_logo || '');
            setBillNote(s.bill_note || '');
            setBillBarcodeEnabled(s.bill_barcode_enabled !== 'false');
            setBillLogoEnabled(s.bill_logo_enabled !== 'false');
            setBarcodeSize(s.barcode_size || 'medium');
            setCustomBarcodeW(s.barcode_custom_w || '50');
            setCustomBarcodeH(s.barcode_custom_h || '25');
            setReceiptPrefix(s.receipt_prefix || 'INV');
            setTaxRate(s.tax_rate || '0');
            setCurrencyCode(s.currency_code || 'PKR');

            // Sync to localStorage
            localStorage.setItem('company_name', s.company_name || '');
            localStorage.setItem('company_logo', s.company_logo || '');
            localStorage.setItem('currency_code', s.currency_code || 'PKR');
            localStorage.setItem('receipt_prefix', s.receipt_prefix || 'INV');
        } catch (err) {
            console.error('Failed to load settings:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveSettings = async () => {
        setSaving(true);
        try {
            const settings = [
                { key: 'company_name', value: shopName },
                { key: 'shop_address', value: shopAddress },
                { key: 'shop_phone', value: shopPhone },
                { key: 'shop_email', value: shopEmail },
                { key: 'bill_note', value: billNote },
                { key: 'bill_barcode_enabled', value: billBarcodeEnabled ? 'true' : 'false' },
                { key: 'bill_logo_enabled', value: billLogoEnabled ? 'true' : 'false' },
                { key: 'barcode_size', value: barcodeSize },
                { key: 'barcode_custom_w', value: customBarcodeW },
                { key: 'barcode_custom_h', value: customBarcodeH },
                { key: 'receipt_prefix', value: receiptPrefix },
                { key: 'tax_rate', value: taxRate },
                { key: 'currency_code', value: currencyCode },
                { key: 'theme', value: theme }
            ];
            await updateSetting(settings);

            // LocalStorage sync
            localStorage.setItem('company_name', shopName);
            localStorage.setItem('shop_address', shopAddress);
            localStorage.setItem('shop_phone', shopPhone);
            localStorage.setItem('bill_note', billNote);
            localStorage.setItem('barcode_size', barcodeSize);
            localStorage.setItem('currency_code', currencyCode);
            localStorage.setItem('theme', theme);
            localStorage.setItem('module_suppliers', moduleSuppliers ? 'enabled' : 'disabled');
            localStorage.setItem('module_expenses', moduleExpenses ? 'enabled' : 'disabled');
            localStorage.setItem('module_team', moduleTeam ? 'enabled' : 'disabled');

            document.documentElement.setAttribute('data-bs-theme', theme);
            window.dispatchEvent(new Event('localStorageChanged'));
            
            showSuccess('System Updated', 'Global configuration committed successfully.');
        } catch (err) {
            showError('Update Failed', err.response?.data?.message || 'Failed to save settings.');
        } finally {
            setSaving(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const formData = new FormData();
        formData.append('logo', file);
        try {
            const res = await updateLogo(formData);
            setLogoUrl(res.data.logo_url);
            localStorage.setItem('company_logo', res.data.logo_url);
            window.dispatchEvent(new Event('localStorageChanged'));
            showSuccess('Branding Asset Updated');
        } catch (err) {
            showError('Upload Failed');
        }
    };

    const sections = [
        { id: 'shop', label: 'Shop Profile', icon: 'bi-shop', desc: 'Business identity & branding' },
        { id: 'pos', label: 'POS & Billing', icon: 'bi-receipt', desc: 'Receipts, taxes & prefix' },
        { id: 'barcode', label: 'Barcodes', icon: 'bi-upc-scan', desc: 'Label sizing & printing' },
        { id: 'system', label: 'System Prefs', icon: 'bi-cpu', desc: 'Theme, currency & modules' },
    ];

    if (loading) return <Layout title="Settings"><div className="d-flex justify-content-center py-5"><div className="spinner-border text-primary"></div></div></Layout>;

    return (
        <Layout title="System Configuration">
            <div className="row g-4 animate__animated animate__fadeIn">
                <div className="col-12 col-xl-3">
                    <div className="card border-0 shadow-sm rounded-4 p-3 bg-white sticky-top settings-nav-card" style={{ top: '100px', zIndex: 10 }}>
                        <div className="p-3 mb-3">
                            <h5 className="fw-bold mb-1">Command Center</h5>
                            <p className="text-muted small mb-0">System-wide parameters</p>
                        </div>
                        {sections.map(s => (
                            <button key={s.id} 
                                className={`btn text-start w-100 rounded-4 p-3 mb-2 d-flex align-items-center gap-3 transition-all ${activeSection === s.id ? 'btn-primary shadow-sm active-nav-btn' : 'btn-light inactive-nav-btn'}`} 
                                onClick={() => setActiveSection(s.id)}>
                                <div className={`icon-box rounded-3 d-flex align-items-center justify-content-center ${activeSection === s.id ? 'active-icon-box' : 'bg-primary bg-opacity-10 text-primary'}`} style={{ width: '40px', height: '40px' }}>
                                    <i className={`bi ${s.icon} fs-5 ${activeSection === s.id ? 'text-white' : ''}`}></i>
                                </div>
                                <div>
                                    <div className="fw-bold small">{s.label}</div>
                                    <div className={`x-small ${activeSection === s.id ? 'text-white text-opacity-75' : 'text-muted'}`}>{s.desc}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="col-12 col-xl-9">
                    {/* Content Area */}
                    <div className="settings-content-card">
                        {/* Shop Profile Section */}
                        {activeSection === 'shop' && (
                            <div className="animate__animated animate__fadeIn">
                                <h5 className="fw-extrabold mb-4 d-flex align-items-center gap-3">
                                    <i className="bi bi-shop text-primary"></i> Business Identity
                                </h5>
                                
                                <div className="d-flex flex-column flex-md-row align-items-center gap-4 p-4 rounded-4 mb-4 bg-light bg-opacity-50 border border-dashed logo-upload-box">
                                    <div className="logo-preview-lg shadow-sm">
                                        {logoUrl ? <img src={logoUrl} alt="Logo" /> : <i className="bi bi-image fs-1 text-muted opacity-25"></i>}
                                    </div>
                                    <div className="text-center text-md-start">
                                        <h6 className="fw-bold mb-1">Global Branding Asset</h6>
                                        <p className="text-muted small mb-3">Visible on all receipts, invoices, and portals.</p>
                                        <div className="d-flex gap-2 justify-content-center justify-content-md-start">
                                            <input type="file" ref={fileInputRef} className="d-none" accept="image/*" onChange={handleLogoUpload} />
                                            <button className="btn btn-primary rounded-pill px-4 fw-bold btn-sm" onClick={() => fileInputRef.current.click()}><i className="bi bi-upload me-2"></i>Upload Logo</button>
                                            {logoUrl && <button className="btn btn-outline-danger rounded-pill px-4 fw-bold btn-sm" onClick={async () => { if((await showConfirmDanger('Remove Logo?')).isConfirmed) { await removeLogo(); setLogoUrl(''); localStorage.removeItem('company_logo'); window.dispatchEvent(new Event('localStorageChanged')); }}}><i className="bi bi-trash me-2"></i>Remove</button>}
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-4">
                                    <div className="col-md-6"><label className="form-label-modern">Business Legal Name</label><input type="text" className="form-control-modern-input" value={shopName} onChange={e => setShopName(e.target.value)} /></div>
                                    <div className="col-md-6"><label className="form-label-modern">Authorized Contact Email</label><input type="email" className="form-control-modern-input" value={shopEmail} onChange={e => setShopEmail(e.target.value)} /></div>
                                    <div className="col-md-6"><label className="form-label-modern">Operation Contact (Phone)</label><input type="text" className="form-control-modern-input" value={shopPhone} onChange={e => setShopPhone(e.target.value)} /></div>
                                    <div className="col-12"><label className="form-label-modern">Registered Physical Address</label><textarea className="form-control-modern-input" rows="2" value={shopAddress} onChange={e => setShopAddress(e.target.value)} style={{height:'80px'}}></textarea></div>
                                </div>
                            </div>
                        )}

                        {/* POS & Billing Section */}
                        {activeSection === 'pos' && (
                            <div className="animate__animated animate__fadeIn">
                                <h5 className="fw-extrabold mb-4 d-flex align-items-center gap-3">
                                    <i className="bi bi-receipt text-primary"></i> Billing & POS Engine
                                </h5>
                                
                                <div className="row g-4 mb-4">
                                    <div className="col-md-6">
                                        <label className="form-label-modern">Invoice Prefix</label>
                                        <input type="text" className="form-control-modern-input" value={receiptPrefix} onChange={e => setReceiptPrefix(e.target.value)} placeholder="e.g. INV" />
                                        <small className="text-muted x-small mt-1">Global prefix for all system-generated invoices.</small>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label-modern">Standard Tax Rate (%)</label>
                                        <input type="number" className="form-control-modern-input" value={taxRate} onChange={e => setTaxRate(e.target.value)} placeholder="0.00" />
                                        <small className="text-muted x-small mt-1">Applied during POS checkout (if enabled).</small>
                                    </div>
                                </div>

                                <div className="bg-light p-4 rounded-4 mb-4 dark-muted-bg">
                                    <h6 className="fw-bold mb-3 small text-uppercase ls-1">Receipt Customization</h6>
                                    <div className="d-flex flex-column gap-3">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div><div className="fw-bold small">Branding on Bill</div><div className="x-small text-muted">Include logo in the receipt header</div></div>
                                            <div className="form-check form-switch"><input className="form-check-input" type="checkbox" checked={billLogoEnabled} onChange={e => setBillLogoEnabled(e.target.checked)} /></div>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div><div className="fw-bold small">Quick-Lookup Barcode</div><div className="x-small text-muted">Print scannable code at bottom</div></div>
                                            <div className="form-check form-switch"><input className="form-check-input" type="checkbox" checked={billBarcodeEnabled} onChange={e => setBillBarcodeEnabled(e.target.checked)} /></div>
                                        </div>
                                    </div>
                                </div>

                                <label className="form-label-modern">Custom Footer/Policy Note</label>
                                <textarea className="form-control-modern-input" rows="3" value={billNote} onChange={e => setBillNote(e.target.value)} placeholder="Exchange within 7 days..." style={{height:'100px'}}></textarea>
                            </div>
                        )}

                        {/* Barcode Section */}
                        {activeSection === 'barcode' && (
                            <div className="animate__animated animate__fadeIn">
                                <h5 className="fw-extrabold mb-4 d-flex align-items-center gap-3">
                                    <i className="bi bi-upc-scan text-primary"></i> Label Printing Protocol
                                </h5>
                                <div className="row g-3 mb-4">
                                    {['small','medium','large','custom'].map(s => (
                                        <div className="col-6 col-md-3" key={s}>
                                            <div className={`p-4 rounded-4 text-center border-2 transition-all cursor-pointer barcode-size-card ${barcodeSize === s ? 'border-primary bg-primary bg-opacity-10 active' : 'border'}`} onClick={() => setBarcodeSize(s)}>
                                                <i className={`bi bi-${s === 'custom' ? 'rulers' : 'aspect-ratio-fill'} fs-3 mb-2 d-block ${barcodeSize === s ? 'text-primary' : 'text-muted'}`}></i>
                                                <div className="fw-bold small text-uppercase">{s}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {barcodeSize === 'custom' && (
                                    <div className="row g-3 p-4 bg-light rounded-4 animate__animated animate__fadeIn dark-muted-bg">
                                        <div className="col-6"><label className="form-label-modern">Width (mm)</label><input type="number" className="form-control-modern-input" value={customBarcodeW} onChange={e => setCustomBarcodeW(e.target.value)} /></div>
                                        <div className="col-6"><label className="form-label-modern">Height (mm)</label><input type="number" className="form-control-modern-input" value={customBarcodeH} onChange={e => setCustomBarcodeH(e.target.value)} /></div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* System Prefs Section */}
                        {activeSection === 'system' && (
                            <div className="animate__animated animate__fadeIn">
                                <h5 className="fw-extrabold mb-4 d-flex align-items-center gap-3">
                                    <i className="bi bi-cpu text-primary"></i> System Environment
                                </h5>

                                <div className="row g-4 mb-4">
                                    <div className="col-md-6">
                                        <label className="form-label-modern">Regional Currency</label>
                                        <select className="form-select form-control-modern-input" value={currencyCode} onChange={e => setCurrencyCode(e.target.value)}>
                                            <option value="PKR">Pakistani Rupee (Rs.)</option>
                                            <option value="USD">US Dollar ($)</option>
                                            <option value="AED">UAE Dirham (AED)</option>
                                            <option value="SAR">Saudi Riyal (SAR)</option>
                                            <option value="INR">Indian Rupee (₹)</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label-modern">Visual Engine Mode</label>
                                        <select className="form-select form-control-modern-input" value={theme} onChange={e => setTheme(e.target.value)}>
                                            <option value="light">High-Contrast Light</option>
                                            <option value="dark">Deep Neural Dark</option>
                                        </select>
                                    </div>
                                </div>

                                <h6 className="fw-bold mb-3 small text-uppercase ls-1">Module Access Control</h6>
                                <div className="d-flex flex-column gap-2">
                                    {[
                                        { id: 'suppliers', label: 'Supplier Ecosystem', val: moduleSuppliers, set: setModuleSuppliers, icon: 'bi-truck' },
                                        { id: 'expenses', label: 'Expense Analytics', val: moduleExpenses, set: setModuleExpenses, icon: 'bi-wallet2' },
                                        { id: 'team', label: 'Workforce Portal', val: moduleTeam, set: setModuleTeam, icon: 'bi-people' }
                                    ].map(mod => (
                                        <div key={mod.id} className="d-flex justify-content-between align-items-center p-3 rounded-4 bg-light module-toggle-card">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="icon-box-sm bg-white rounded-3 d-flex align-items-center justify-content-center" style={{width:'32px', height:'32px'}}><i className={`bi ${mod.icon} text-primary`}></i></div>
                                                <div className="fw-bold small">{mod.label}</div>
                                            </div>
                                            <div className="form-check form-switch"><input className="form-check-input" type="checkbox" checked={mod.val} onChange={e => mod.set(e.target.checked)} /></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <hr className="my-5 opacity-50" />

                        <div className="text-end">
                            <button className="btn btn-primary btn-lg rounded-pill px-5 py-3 fw-extrabold shadow-lg" onClick={handleSaveSettings} disabled={saving}>
                                {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Committing...</> : <><i className="bi bi-shield-check me-2"></i>Commit Changes</>}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                [data-bs-theme="dark"] .settings-nav-card,
                [data-bs-theme="dark"] .settings-content-card { background: #151521 !important; border: 1px solid #2b2b40 !important; }
                [data-bs-theme="dark"] .inactive-nav-btn { background: transparent !important; color: #a1a5b7 !important; }
                [data-bs-theme="dark"] .inactive-nav-btn:hover { background: #1b1b28 !important; }
                [data-bs-theme="dark"] .logo-upload-box { background: #1b1b28 !important; border-color: #2b2b40 !important; }
                [data-bs-theme="dark"] .form-control-modern-input { background: #1b1b28 !important; border-color: #2b2b40 !important; color: white; }
                [data-bs-theme="dark"] .dark-muted-bg,
                [data-bs-theme="dark"] .module-toggle-card { background: #1b1b28 !important; color: white; }
                [data-bs-theme="dark"] .barcode-size-card { border-color: #2b2b40 !important; }
                [data-bs-theme="dark"] .barcode-size-card:not(.active):hover { background: #1b1b28 !important; }

                .active-nav-btn { background: var(--primary-color) !important; color: white !important; }
                .active-icon-box { background: rgba(255, 255, 255, 0.25) !important; color: white !important; }

                .settings-content-card { background: white; border-radius: 24px; padding: 40px; box-shadow: 0 10px 30px rgba(0,0,0,0.02); min-height: 600px; }
                .logo-preview-lg { width: 120px; height: 120px; border-radius: 30px; background: white; border: 2px solid #eff2f5; display: flex; align-items: center; justify-content: center; overflow: hidden; }
                .logo-preview-lg img { width: 100%; height: 100%; object-fit: contain; padding: 10px; }
                .form-label-modern { font-size: 11px; font-weight: 800; text-transform: uppercase; color: #a1a5b7; margin-bottom: 8px; letter-spacing: 0.5px; }
                .form-control-modern-input { background: #f5f8fa; border: 1px solid transparent; border-radius: 12px; padding: 12px 15px; font-weight: 600; font-size: 14px; width: 100%; transition: all 0.3s; }
                .form-control-modern-input:focus { background: white; border-color: var(--primary-color); outline: none; box-shadow: 0 0 0 4px rgba(108, 93, 211, 0.1); }
                .cursor-pointer { cursor: pointer; }
                .transition-all { transition: all 0.3s ease; }
                .ls-1 { letter-spacing: 1px; }
                @media (max-width: 991px) { .sticky-top { position: relative !important; top: 0 !important; margin-bottom: 20px; } .settings-content-card { padding: 20px; } }
            `}</style>
        </Layout>
    );
};

export default Settings;
